import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { Box, HStack, IconButton, ModalBase, Text } from '@/presentation/components'
import { ImageProps, selectedOsImageViewState } from './atom'
import { StyleSheet } from 'react-native'
import { useMediaQueries } from '@/presentation/hooks'

type OsImageViewProps = {
}

export const OsImageView: React.FC<OsImageViewProps> = () => {
  const [imageView, setImageView] = useRecoilState(selectedOsImageViewState)
  const [playbackError, setPlaybackError] = useState(false)
  const [mediaSize, setMediaSize] = useState<{ width: number, height: number } | null>(null)
  const { width, height } = useMediaQueries()

  const maxMediaWidth = Math.max(Math.min(width - 128, 1216), 280)
  const maxMediaHeight = Math.max(height - 180, 180)
  const playbackMessage = imageView?.message ?? (playbackError ? 'Não foi possível reproduzir este arquivo. Você ainda pode baixá-lo.' : '')

  useEffect(() => {
    setPlaybackError(false)
    setMediaSize(null)
  }, [imageView])

  const fitMedia = (mediaWidth: number, mediaHeight: number): void => {
    if (!mediaWidth || !mediaHeight) return

    const scale = Math.min(maxMediaWidth / mediaWidth, maxMediaHeight / mediaHeight, 1)
    setMediaSize({
      width: Math.floor(mediaWidth * scale),
      height: Math.floor(mediaHeight * scale)
    })
  }

  const handleVideoMetadata = (event: React.SyntheticEvent<HTMLVideoElement>): void => {
    fitMedia(event.currentTarget.videoWidth, event.currentTarget.videoHeight)
  }

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>): void => {
    fitMedia(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)
  }

  const handleClose = (): void => {
    setImageView(null as unknown as ImageProps)
  }

  return (
    <ModalBase maxWidth={1280} maxHeight={height - 24} visible={!!imageView} onClose={handleClose}>
      <Box style={styles.container}>
        <HStack justify='between' items='center'>
          <Text variant="subtitle1" style={{ fontWeight: '500' }}>{imageView?.description}</Text>
          <IconButton
            icon={<MaterialCommunityIcons name="close" size={24} />}
            onPress={handleClose}
          />
        </HStack>

        {playbackMessage
          ? <Text variant="body2">{playbackMessage}</Text>
          : imageView?.type === 'video'
            ? <video
                controls
                autoPlay
                onLoadedMetadata={handleVideoMetadata}
                onError={() => { setPlaybackError(true) }}
                style={{
                  ...styles.media,
                  maxWidth: maxMediaWidth,
                  maxHeight: maxMediaHeight,
                  ...(mediaSize ?? {})
                }}
              >
                <source src={imageView.uri} type={imageView.mimeType ?? 'video/mp4'} />
                Seu navegador não suporta a reprodução deste vídeo.
              </video>
            : imageView?.type === 'audio'
              ? <audio controls autoPlay onError={() => { setPlaybackError(true) }} style={{ width: '100%' }}>
                  <source src={imageView.uri} type={imageView.mimeType ?? 'audio/mpeg'} />
                  Seu navegador não suporta a reprodução deste áudio.
                </audio>
              : <img
                  src={imageView?.uri}
                  alt={imageView?.description}
                  onLoad={handleImageLoad}
                  onError={() => { setPlaybackError(true) }}
                  style={{
                    ...styles.media,
                    maxWidth: maxMediaWidth,
                    maxHeight: maxMediaHeight,
                    ...(mediaSize ?? {})
                  }}
                />
        }
      </Box>
    </ModalBase>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: '100%',
    gap: 8
  },
  media: {
    display: 'block',
    alignSelf: 'center',
    maxWidth: '100%',
    objectFit: 'contain' as 'contain'
  }
})
